import React, { useRef, useState } from 'react';
// Styles provided by RestaurantOnboarding.css

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_MB = 5;
const MAX_SIDE = 2000; // px — imágenes más grandes se reducen antes de enviar

/* Reduce la imagen en el navegador si su lado mayor supera MAX_SIDE.
   Logos exportados de Canva pueden venir en 6000px+ y tumban el backend. */
const downscaleIfNeeded = (dataUrl, type) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxSide = Math.max(img.width, img.height);
      if (maxSide <= MAX_SIDE) return resolve(dataUrl);
      const scale  = MAX_SIDE / maxSide;
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      // JPEG comprimido para fotos; PNG para conservar transparencia
      resolve(type === 'image/jpeg'
        ? canvas.toDataURL('image/jpeg', 0.85)
        : canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl); // si no se puede leer, decide el backend
    img.src = dataUrl;
  });

const StepRestaurantPhoto = ({ formData, onUpdate, errors = {} }) => {
  const inputRef  = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const processFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      onUpdate({ photo: null, photo_error: 'Solo se aceptan imágenes JPG, PNG o WEBP' });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      onUpdate({ photo: null, photo_error: `La imagen no puede superar ${MAX_MB} MB` });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const photo = await downscaleIfNeeded(e.target.result, file.type);
      onUpdate({ photo, photo_error: null });
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files) => {
    if (files?.length) processFile(files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemove = () => {
    onUpdate({ photo: null, photo_error: null });
    if (inputRef.current) inputRef.current.value = '';
  };

  const hasPhoto  = !!formData.photo;
  const photoErr  = formData.photo_error || errors.photo;

  return (
    <div className="ro-body ro-basic">

      <div className="ro-step-hd">
        <span className="ro-eyebrow ro-eyebrow--accent">Paso 2 · Imagen</span>
        <h2 className="ro-title">Logotipo de tu negocio</h2>
        <p className="ro-subtitle">
          Una buena imagen genera más confianza en tus clientes.
        </p>
      </div>

      {hasPhoto ? (
        /* ── Preview ── */
        <div className="ro-photo-preview-wrap">
          <img
            src={formData.photo}
            alt="Vista previa"
            className="ro-photo-preview-img"
          />
          <div className="ro-photo-preview-actions">
            <button
              type="button"
              className="ro-photo-change-btn"
              onClick={() => inputRef.current?.click()}
            >
              ↺ Cambiar imagen
            </button>
            <button
              type="button"
              className="ro-photo-remove-btn"
              onClick={handleRemove}
            >
              Eliminar
            </button>
          </div>
        </div>
      ) : (
        /* ── Drop zone ── */
        <div
          className={`ro-photo-zone${dragOver ? ' ro-photo-zone--over' : ''}${photoErr ? ' ro-photo-zone--err' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          aria-label="Subir imagen"
        >
          <div className="ro-photo-zone-ico" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="3" ry="3"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <p className="ro-photo-zone-txt">
            <strong>Arrastra tu imagen aquí</strong>
            <br />
            o haz clic para explorar
          </p>
          <p className="ro-photo-zone-hint">JPG, PNG o WEBP · Máx. {MAX_MB} MB</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {photoErr && (
        <span className="ro-err-msg ro-err-msg--block">{photoErr}</span>
      )}

      <p className="ro-photo-tips">
          <strong>Consejo:</strong> Sube una imagen clara y en alta resolución donde el logotipo se vea completo.
      </p>

    </div>
  );
};

export default StepRestaurantPhoto;
